/**
 * Basic api call for the admins dashboard statistics
 */

import axios from "@/utils/axios";
import {
  AdminLogEntry,
  AdminSystemStatistics,
  InstanceMigrationJob,
  InstanceMigrationStartRequest,
  InstanceMigrationStatus,
  SearchIndexRebuildResponse,
  SearchIndexStatus,
  StatisticsRebuildJob,
} from "@/types/adminSystem.types";
import { AccountActivity } from "@/types/activity.types";
import { PagedResponse } from "@/types/pagination.types";

export const getSystemStatistics = async (): Promise<AdminSystemStatistics> => {
  const response = await axios.get("/admin/system/statistics");
  return response.data;
};

export const getAllUserActivities = async (): Promise<AccountActivity[]> => {
  const reponse = await axios.get("/admin/users/activities");
  return reponse.data;
};

export const restoreActivity = async (id: string): Promise<void> => {
  await axios.post(`/admin/users/activities/${id}/restore`);
};

export const startStatisticsRebuild = async (): Promise<StatisticsRebuildJob> => {
  const response = await axios.post("/admin/system/statistics/rebuild");
  return response.data as StatisticsRebuildJob;
};

export const getLatestStatisticsRebuild = async (): Promise<StatisticsRebuildJob | null> => {
  const response = await axios.get("/admin/system/statistics/rebuild", {
    validateStatus: (status) => status === 200 || status === 204,
  });
  if (response.status === 204) {
    return null;
  }
  return response.data as StatisticsRebuildJob;
};

export const startSearchIndexRebuild = async (): Promise<SearchIndexRebuildResponse> => {
  const response = await axios.post("/admin/search-index/rebuild");
  return response.data as SearchIndexRebuildResponse;
};

export const startInstanceMigration = async (
  request?: InstanceMigrationStartRequest
): Promise<InstanceMigrationJob> => {
  const response = await axios.post(
    "/admin/system/instance-migration/start",
    request ?? {}
  );
  return response.data as InstanceMigrationJob;
};

export const getInstanceMigrationStatus = async (): Promise<InstanceMigrationStatus> => {
  const response = await axios.get("/admin/system/instance-migration");
  return response.data as InstanceMigrationStatus;
};

export const getSearchIndexStatus = async (): Promise<SearchIndexStatus> => {
  const response = await axios.get("/admin/search-index/status");
  return response.data as SearchIndexStatus;
};

export interface AdminLogQuery {
  source?: "application" | "parquet" | "";
  level?: "TRACE" | "DEBUG" | "INFO" | "WARN" | "ERROR" | "";
  loggerContains?: string;
  query?: string;
  page?: number;
  size?: number;
}

export const getAdminLogs = async (
  params: AdminLogQuery
): Promise<PagedResponse<AdminLogEntry>> => {
  const query = new URLSearchParams();

  if (params.source) query.set("source", params.source);
  if (params.level) query.set("level", params.level);
  if (params.loggerContains && params.loggerContains.trim()) {
    query.set("loggerContains", params.loggerContains.trim());
  }
  if (params.query && params.query.trim()) {
    query.set("query", params.query.trim());
  }
  query.set("page", String(Math.max(0, params.page ?? 0)));
  query.set("size", String(Math.max(1, Math.min(200, params.size ?? 100))));

  const response = await axios.get<PagedResponse<AdminLogEntry>>(
    `/admin/system/logs?${query.toString()}`
  );
  return response.data;
};
